import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { api } from "../../api/client";
import PortalSidebar from "../../components/portal/PortalSidebar";
import { 
  Users, FileText, Clock, TrendingUp, AlertCircle, 
  ChevronRight
} from "lucide-react";
import { Link } from "react-router-dom";

export default function ClientDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentPatients, setRecentPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [statsRes, patientsRes] = await Promise.all([
        api.get("/portal/dashboard/stats"),
        api.get("/portal/patients?limit=5"),
      ]);
      setStats(statsRes.data);
      setRecentPatients(patientsRes.data.patients || []);
    } catch (err) {
      console.error("Failed to fetch dashboard data:", err);
      setError("Failed to load dashboard data");
      // Honest state on every failure path, not just the API's own
      // "unavailable" answer: a 403 (no firm associated) used to leave
      // `stats` null, which fell through to a grid of fabricated zeros —
      // $0 balance, 0 active cases — presented as fact.
      setStats({
        available: false,
        reason: err?.response?.status === 403 ? "no_firm" : "temporarily_unavailable",
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'ACTIVE': 'bg-emerald-100 text-emerald-700',
      'PENDING': 'bg-amber-100 text-amber-700',
      'DISCHARGED': 'bg-slate-100 text-slate-600',
      'ON_HOLD': 'bg-red-100 text-red-700',
    };
    return colors[status] || 'bg-slate-100 text-slate-600';
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <PortalSidebar />

      {/* Main content */}
      <main className="flex-1 p-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-slate-900">
              Welcome back{user?.email ? `, ${user.email.split('@')[0]}` : ''}
            </h1>
            <p className="text-slate-500 mt-1">
              Here's an overview of your patients and cases.
            </p>
          </div>

          {error && stats?.available !== false && (
            <div className="mb-6 flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <>
              {/* Foundation D (2026-09-05): when the warehouse is
                  unreachable the API says so instead of inventing case
                  counts and a balance — so say so here too. */}
              {stats?.available === false && (
                <div
                  data-testid="portal-stats-unavailable"
                  className="mb-8 rounded-xl border border-amber-200 bg-amber-50 px-5 py-4"
                >
                  {stats.reason === "no_firm" ? (
                    <>
                      <p className="text-sm font-semibold text-amber-900">
                        No firm is associated with this account
                      </p>
                      <p className="text-sm text-amber-800 mt-0.5">
                        Contact WestStar to have your account linked to your firm.
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="text-sm font-semibold text-amber-900">
                        Case figures are temporarily unavailable
                      </p>
                      <p className="text-sm text-amber-800 mt-0.5">
                        We can't reach the records system right now. Your cases are
                        unaffected — please check back shortly.
                      </p>
                    </>
                  )}
                </div>
              )}

              {/* Stats Cards */}
              <div
                className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8 ${
                  stats?.available === false ? "hidden" : ""
                }`}
              >
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500 font-medium">Total Patients</p>
                      <p className="text-3xl font-bold text-slate-900 mt-1">
                        {stats?.totalPatients || 0}
                      </p>
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                      <Users size={24} className="text-blue-600" />
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl border border-slate-200 p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500 font-medium">Active Cases</p>
                      <p className="text-3xl font-bold text-emerald-600 mt-1">
                        {stats?.activeCases || 0}
                      </p>
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                      <TrendingUp size={24} className="text-emerald-600" />
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl border border-slate-200 p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500 font-medium">Pending Liens</p>
                      <p className="text-3xl font-bold text-amber-600 mt-1">
                        {stats?.pendingLiens || 0}
                      </p>
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                      <FileText size={24} className="text-amber-600" />
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl border border-slate-200 p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500 font-medium">Total Balance</p>
                      <p className="text-3xl font-bold text-slate-900 mt-1">
                        ${(stats?.totalBalance || 0).toLocaleString()}
                      </p>
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                      <Clock size={24} className="text-purple-600" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Recent Patients */}
              <div className="bg-white rounded-xl border border-slate-200">
                <div className="p-6 border-b border-slate-200 flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-slate-900">Recent Patients</h2>
                  <Link
                    to="/portal/patients"
                    className="text-sm text-emerald-600 hover:text-emerald-700 font-medium flex items-center gap-1"
                  >
                    View all
                    <ChevronRight size={16} />
                  </Link>
                </div>
                <div className="divide-y divide-slate-100">
                  {recentPatients.length === 0 ? (
                    <div
                      className="p-8 text-center text-slate-500"
                      data-testid="portal-recent-patients-empty"
                    >
                      {stats?.available === false
                        ? "This list can't be loaded right now."
                        : "No patients found. Add your first patient to get started."}
                    </div>
                  ) : (
                    recentPatients.map((patient) => (
                      <Link
                        key={patient.id}
                        to={`/portal/patients/${patient.id}`}
                        className="flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center text-slate-600 font-medium text-sm">
                            {patient.first_name?.[0]}{patient.last_name?.[0]}
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">
                              {patient.first_name} {patient.last_name}
                            </p>
                            <p className="text-sm text-slate-500">
                              {patient.case_number || 'No case number'} • {patient.treating_clinic || 'Unknown clinic'}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(patient.status)}`}>
                            {patient.status || 'Unknown'}
                          </span>
                          <ChevronRight size={18} className="text-slate-400" />
                        </div>
                      </Link>
                    ))
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
